import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Checkbox } from './checkbox';
import api, { registrationAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { validateCustomFields } from '../../utils/formFieldValidation';
import CustomQuestionsFieldset, { type CustomQuestionField } from './CustomQuestionsFieldset';

interface GuestRegistrationFormProps {
  eventId: string;
  guestFormFields?: CustomQuestionField[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
  loading?: boolean;
}

export default function GuestRegistrationForm({
  eventId,
  guestFormFields = [],
  onSubmit,
  onCancel,
  loading = false
}: GuestRegistrationFormProps) {
  const { success, error } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    notes: '',
    // string for most answers, boolean for a single tick, string[] for multi-select
    customFields: {} as { [key: string]: string | boolean | string[] }
  });
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);
  const [emailStatus, setEmailStatus] = useState<{
    available: boolean | null;
    isExistingUser: boolean | null;
    message: string | null;
  }>({ available: null, isExistingUser: null, message: null });
  const [existingRegistrationId, setExistingRegistrationId] = useState<string | null>(null);

  // Debounced email check
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.email && formData.email.includes('@')) {
        setEmailChecking(true);
        try {
          const result = await registrationAPI.checkEmailAvailability(eventId, formData.email);
          setEmailAvailable(result.available);
          setEmailStatus({
            available: result.available,
            isExistingUser: result.isExistingUser,
            message: result.message
          });
          
          if (!result.available) {
            if (result.isExistingUser) {
              // Existing Udyogapramoda user - show message with sign in link
              setFieldErrors(prev => ({
                ...prev,
                email: 'existing_user'
              }));
            } else {
              // Already registered as guest
              setExistingRegistrationId(result.registrationId);
              setFieldErrors(prev => ({
                ...prev,
                email: 'User with this email ID already registered as guest for this event'
              }));
            }
          } else {
            setFieldErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.email;
              return newErrors;
            });
          }
        } catch (err: any) {
          console.error('Email check error:', err);
          setEmailAvailable(null);
          setEmailStatus({ available: null, isExistingUser: null, message: null });
        } finally {
          setEmailChecking(false);
        }
      } else {
        setEmailAvailable(null);
        setEmailStatus({ available: null, isExistingUser: null, message: null });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email, eventId]);

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    if (field.startsWith('custom_')) {
      const customFieldName = field.replace('custom_', '');
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [customFieldName]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Required fields
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    // Custom fields validation. Rules live in utils/formFieldValidation so they
    // can be tested on their own; the form only merges the result.
    Object.assign(errors, validateCustomFields(guestFormFields as any, formData.customFields));

    // Email availability check
    if (emailAvailable === false) {
      if (emailStatus.isExistingUser) {
        errors.email = 'existing_user';
      } else {
        errors.email = 'User with this email ID already registered as guest for this event';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWithdrawRegistration = async () => {
    if (!existingRegistrationId) return;
    
    try {
      await api.post(`/registrations/guest/${existingRegistrationId}/withdraw`);
      success('Registration withdrawn successfully');
      // Clear the form and allow re-registration
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organization: '',
        notes: '',
        customFields: {}
      });
      setEmailAvailable(null);
      setEmailStatus({ available: null, isExistingUser: null, message: null });
      setExistingRegistrationId(null);
      setFieldErrors({});
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to withdraw registration');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (emailChecking) {
      error('Please wait while we check email availability');
      return;
    }

    if (emailAvailable === false) {
      if (emailStatus.isExistingUser) {
        error('You are a registered user of udyogapramoda.org. Please sign in to register for this event.');
      } else {
        error('User with this email ID already registered as guest for this event');
      }
      return;
    }

    try {
      // Send a structured copy alongside the readable map: it carries each
      // question's permanent id, the wording as it stood when this person
      // answered, and multi-select answers as real arrays.
      const customFieldResponses = guestFormFields
        .map((field) => ({
          fieldId: field.fieldId || field.fieldName,
          fieldName: field.fieldName,
          label: field.fieldLabel,
          fieldType: field.fieldType,
          value: formData.customFields[field.fieldName],
        }))
        .filter((r) => r.value !== undefined && r.value !== '');

      // The map is what the admin and confirmation screens display, so arrays
      // are joined into something readable there.
      const readableCustomFields: { [key: string]: string | boolean } = {};
      for (const r of customFieldResponses) {
        readableCustomFields[r.fieldName] = Array.isArray(r.value)
          ? r.value.join(', ')
          : (r.value as string | boolean);
      }

      await onSubmit({ ...formData, customFields: readableCustomFields, customFieldResponses });
    } catch (err: any) {
      error(err.response?.data?.message || 'Registration failed');
    }
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={fieldErrors.firstName ? 'border-red-500' : ''}
          />
          {fieldErrors.firstName && <p className="text-sm text-red-500">{fieldErrors.firstName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">
            Last Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={fieldErrors.lastName ? 'border-red-500' : ''}
          />
          {fieldErrors.lastName && <p className="text-sm text-red-500">{fieldErrors.lastName}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">
          Email <span className="text-red-500">*</span>
        </Label>
        <div className="relative">
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`${fieldErrors.email ? 'border-red-500' : ''} ${emailChecking ? 'pr-10' : ''}`}
          />
          {emailChecking && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        {fieldErrors.email && fieldErrors.email === 'existing_user' ? (
          <div className="text-sm text-red-500">
            <p>You are a registered user of udyogapramoda.org. Please{' '}
              <a href="/login" className="text-blue-600 hover:text-blue-800 underline">
                sign in
              </a>{' '}
              to register for this event.
            </p>
          </div>
        ) : fieldErrors.email && fieldErrors.email.includes('already registered as guest') ? (
          <div className="text-sm text-red-500">
            <p>{fieldErrors.email}</p>
            <p className="mt-1">
              <button
                type="button"
                onClick={() => handleWithdrawRegistration()}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Click here to withdraw registration
              </button>
            </p>
          </div>
        ) : fieldErrors.email ? (
          <p className="text-sm text-red-500">{fieldErrors.email}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">
          Phone Number <span className="text-red-500">*</span>
        </Label>
        <Input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          className={fieldErrors.phone ? 'border-red-500' : ''}
        />
        {fieldErrors.phone && <p className="text-sm text-red-500">{fieldErrors.phone}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="organization">Organization</Label>
        <Input
          id="organization"
          value={formData.organization}
          onChange={(e) => handleInputChange('organization', e.target.value)}
        />
      </div>

      {/* One shared renderer, so members and guests answer identically. */}
      <CustomQuestionsFieldset
        fields={guestFormFields as any}
        answers={formData.customFields}
        errors={fieldErrors}
        onChange={(fieldName, value) => handleInputChange(`custom_${fieldName}`, value as any)}
        onToggleOption={(fieldName, option, checked) => {
          // Applied against the latest state, so two quick ticks do not
          // overwrite one another.
          setFormData((prev) => {
            const current = Array.isArray(prev.customFields[fieldName])
              ? (prev.customFields[fieldName] as string[])
              : [];
            return {
              ...prev,
              customFields: {
                ...prev.customFields,
                [fieldName]: checked
                  ? [...current, option]
                  : current.filter((o) => o !== option),
              },
            };
          });
        }}
      />

      <div className="space-y-2">
        <Label htmlFor="notes">Additional Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Any additional information you'd like to share..."
        />
      </div>

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading || emailChecking || emailAvailable === false}>
          {loading ? 'Registering...' : 'Register for Event'}
        </Button>
      </div>
    </form>
  );
} 